/**
 * @file   src\Pages\MembershipMonthlyReport.tsx
 * @brief  Membership Monthly Report page for each carwash.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  Button,
  useParams,
  Pagination,
  React,
} from "../utils/thirdpartyComponents";
import { userType, serviceType } from "../utils/enums";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, RevenueApi, LocationApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import CWSelect from "../components/CWSelect";
import { MessageToaster } from "../utils/toastUtil";
import { LocationItem, Option } from "../interfaces/subscriptionInterface";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MONTHS_OPTIONS } from "../utils/constants";

interface MembershipMonthlyRecord {
  TotalRows?: number;
  SubscriptionID?: number;
  LocationName?: string;
  WashName?: string;
  BayType?: number;
  TransactionMonth?: number;
  Renewals?: number | null;
  TotalActiveMembers?: number | null;
  NewSignup?: number | null;
  UserCancellation?: number | null;
  Revenue?: number | null;
}

const MembershipMonthlyReport = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;

  const navigate = useNavigate();

  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reportRecords, setReportRecords] = useState<MembershipMonthlyRecord[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  
  const [filters, setFilters] = useState({
    month: "",
    year: "",
    bayType: "",
    locationId: "0", // "0" represents "All"
  });

  const toaster = new MessageToaster();
  const timezoneOffset = new Date().getTimezoneOffset();

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const pageSize = 10;

  // To display heading
  useEffect(() => {
    getCompany();
    getCompanyAddress();
    getCompanyLocations();
  }, []);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filters.month && filters.year) {
      getMembershipMonthlyReport(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async () => {
    try {
      const carwashGuid = params.id || "";
      const { data } =  await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
      
      if (data.error === 0) {
        const location_list: LocationItem[] = data?.data;

        // Transform the array
        const transformedArray: Option[] = location_list.map((item) => ({
          label: item.locationName,
          value: item.locationId,
        }));

        // Add "All" option at the beginning
        setLocations([{ label: "All", value: 0 }, ...transformedArray]);
      } else {
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = async () => {
    await updateData({
      heading: "Membership Monthly Report",
      url: ``,
      showBackButton:false,
      showMainMenu: true,
      activeLink: "reports" ,
    });
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getBayTypeLabel = (bayType: number) => {
    const bayTypeMap: { [key: number]: string } = {
      [serviceType.Inbay]: "In Bay/Tunnel",
      [serviceType.SelfServe]: "Self Serve",
      [serviceType.DogWash]: "Dog Wash",
      [serviceType.Vacuum]: "Vacuum",
    };
    return bayTypeMap[bayType] || "Unknown";
  };

  const getMonthName = (monthNumber: string) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const monthIndex = parseInt(monthNumber) - 1;
    return monthNames[monthIndex] || monthNumber;
  };

  // Common function to fetch report data from API
  const fetchReportData = async (pageNo: number, pageSizeParam: number) => {
    if (filters.month === '' || filters.year === '') {
      return null;
    }

    const queryParams = new URLSearchParams();

    // pagination
    const offset = timezoneOffset;
    queryParams.append("offset", offset.toString());
    queryParams.append("pageNo", pageNo.toString());
    queryParams.append("pageSize", pageSizeParam.toString());

    // required carwash
    if (params.id) {
      queryParams.append("carWashId", params.id.toString());
    }

    // filters
    if (filters.bayType && filters.bayType !== "all") queryParams.append("bayType", filters.bayType);
    if (filters.month) queryParams.append("month", filters.month);
    if (filters.year) queryParams.append("year", filters.year);
    // Only include locationId if it's not "0" (All)
    if (filters.locationId && filters.locationId !== "0") {
      queryParams.append("locationId", filters.locationId);
    }

    const apiUrl = RevenueApi.CARWASH_OWNER_MEMBERSHIP_MONTHLY_REPORT;
    
    const url = `${apiUrl}?${queryParams.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0) {
      // Check if data.data is an array or has a list property
      let records = [];
      let totalCount = 0;
      
      if (Array.isArray(data?.data)) {
        records = data.data;
        totalCount = data?.TotalRows || records.length;
      } else if (data?.data?.list && Array.isArray(data.data.list)) {
        records = data.data.list;
        totalCount = data.data.totalCount || data?.TotalRows || records.length;
      } else if (Array.isArray(data)) {
        records = data;
        totalCount = records.length;
      }
      
      const limit = data?.data?.limit || pageSizeParam || pageSize;
      
      return {
        list: records,
        totalCount: totalCount,
        limit: limit
      };
    }
    
    return null;
  };

  const getMembershipMonthlyReport = async (pageNo = 1) => {
    try {
      setLoading(true);
      const result = await fetchReportData(pageNo, pageSize);

      if (result) {
        if (result.list?.length > 0) {
          setReportRecords(result.list);
          setTotalPages(Math.ceil(result.totalCount / result.limit) || 1);
        } else {
          setReportRecords([]);
        }
      } else {
        setReportRecords([]);
      }
    } catch (error: any) {
      console.error("Error fetching membership monthly report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to fetch report");
      setReportRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    if (!filters.month || !filters.year) {
      toaster.toastError("Please select month and year before downloading report.");
      return;
    }

    try {
      setLoading(true);
      const result = await fetchReportData(1, 0); // pageNo = 1, pageSize = 0 to get all records

      if (result && result.list?.length > 0) {
        const worksheetData = result.list.map((item: any) => ({
          Month: item?.TransactionMonth ? getMonthName(item.TransactionMonth.toString()) : "",
          Location: item?.LocationName ?? "",
          "Bay Type": getBayTypeLabel(item.BayType),
          "Membership Name": item?.WashName ?? "",
          "Total Active Members": item?.TotalActiveMembers ?? 0,
          "New Signups": item?.NewSignup ?? 0,
          Renewals: item?.Renewals ?? 0,
          Cancellations: item?.UserCancellation ?? 0,
          "Revenue from Memberships ($)": item?.Revenue ?? 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "MembershipMonthly");

        const workbookArray = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([workbookArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `MembershipMonthly_${filters.year || "AllYears"}_${filters.month || "AllMonths"}.xlsx`;
        saveAs(blob, fileName);
      } else {
        toaster.toastError("No records to download.");
      }
    } catch (error: any) {
      console.error("Error downloading membership monthly report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to download report");
    } finally {
      setLoading(false);
    }
  };

  // Navigate according to url
  const itemClick = (url: string) => {
    navigate(url);
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const BayTypeOptions = [
    { label: "All", value: "all" },
    { label: "In Bay/Tunnel", value: serviceType.Inbay.toString() },
    { label: "Self Serve", value: serviceType.SelfServe.toString() },
    { label: "Dog Wash", value: serviceType.DogWash.toString() },
    { label: "Vacuum", value: serviceType.Vacuum.toString() },
  ];

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 2024 + 1 },
    (_, i) => {
      const year = 2024 + i;
      return { label: year.toString(), value: year.toString() };
    }
  );

  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap">
      <Container>
        <Breadcrumb>
          {carwash_owner ? (
            <>
              <Breadcrumb.Item
                onClick={() => itemClick(`/${pageURLs.dashboard}`)}
              >
                Dashboard
              </Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>Membership Monthly Report</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
        
              <CWSelect
                id="selectyear"
                name="selectyear"
                options={yearOptions}
                label="Select Year"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  handleFilterChange("year", option[0]?.value?.toString() || "");
                }}
              />
              <CWSelect
                id="selectmonth"
                name="selectmonth"
                options={MONTHS_OPTIONS}
                label="Select Month"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  handleFilterChange("month", option[0]?.value?.toString() || "");
                }}
              />
              <CWSelect
                id="selectlocation"
                name="selectlocation"
                options={locations}
                label="Select Location"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select Location"
                onChange={(selected: any) => {
                  const value = Array.isArray(selected) && selected.length > 0 ? selected[0].value : "";
                  handleFilterChange("locationId", value.toString());
                }}
              />
              <CWSelect
                id="baytype"
                name="baytype"
                options={BayTypeOptions}
                label="Select Bay Type"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) =>
                  handleFilterChange("bayType", option[0]?.value?.toString() || "")
                }
              />
              <div className="button-group d-flex">
                <Button
                  className="primary-btn"
                  variant="primary"
                  title="Run Report"
                  onClick={() => {
                    // Only fetch when all filters are selected
                    if (filters.month && filters.year) {
                      setCurrentPage(1); // Reset to first page
                      getMembershipMonthlyReport(1);
                    } else {
                      toaster.toastError("Please select month and year before generating report.");
                    }
                  }}
                >
                  Run Report
                </Button>
                <Button
                  className="secondary-btn ms-3"
                  variant="outline-primary"
                  title="Export Report"
                  onClick={downloadReportAsExcel}
                >
                  Export Report
                </Button>
              </div>
 
            </div>
          </div>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>Month</th>
                <th>Location</th>
                <th>Bay Type</th>
                <th>Membership Name</th>
                <th>Total Active Members</th>
                <th>New Signups</th>
                <th>Renewals</th>
                <th>Cancellations</th>
                <th>Revenue from Memberships ($)</th>
              </tr>
            </thead>
            <tbody>
              {reportRecords.length > 0 ? (
                reportRecords.map((item: any, index) => (
                  <tr key={index}>
                    <td>{item?.TransactionMonth ? getMonthName(item.TransactionMonth.toString()) : ""}</td>
                    <td>{item?.LocationName ?? ""}</td>
                    <td>{getBayTypeLabel(item.BayType)}</td>
                    <td>{item?.WashName ?? ""}</td>
                    <td>{item?.TotalActiveMembers ?? 0}</td>
                    <td>{item?.NewSignup ?? 0}</td>
                    <td>{item?.Renewals ?? 0}</td>
                    <td>{item?.UserCancellation ?? 0}</td>
                    <td>${(item?.Revenue ?? 0).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center">
                    {loading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {reportRecords && reportRecords?.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
    </div>
  );
};

export default MembershipMonthlyReport;


